/**
 * Yahtzee Game Engine
 *
 * Core game logic: dice rolling, holding/releasing, scoring calculation,
 * category validation, turn management, upper bonus tracking,
 * Yahtzee bonus (joker) rules, and win condition detection.
 */

import {
  Die,
  DieValue,
  ScoreCategory,
  Scorecard,
  ScoringResult,
  YahtzeePlayer,
  YahtzeePlayerType,
  YahtzeeGameState,
  YahtzeeGameStatus,
  YahtzeeGameSettings,
  YahtzeeDifficulty,
  ALL_CATEGORIES,
  UPPER_CATEGORIES,
  LOWER_CATEGORIES,
  NUM_DICE,
  MAX_ROLLS,
  TOTAL_ROUNDS,
  UPPER_BONUS_THRESHOLD,
  UPPER_BONUS_SCORE,
  YAHTZEE_SCORE,
  YAHTZEE_BONUS_SCORE,
  FULL_HOUSE_SCORE,
  SMALL_STRAIGHT_SCORE,
  LARGE_STRAIGHT_SCORE,
} from './types';

export class YahtzeeEngine {
  private state: YahtzeeGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  private createEmptyState(): YahtzeeGameState {
    return {
      gameId: this.generateId(),
      players: [],
      currentPlayerIndex: 0,
      currentRound: 1,
      dice: this.createFreshDice(),
      rollsRemaining: MAX_ROLLS,
      gameStatus: { type: 'setup' },
      startedAt: Date.now(),
      lastActionAt: Date.now(),
    };
  }

  initializeGame(settings: YahtzeeGameSettings): void {
    const players: YahtzeePlayer[] = [];

    if (settings.mode === 'singleplayer') {
      // Human player
      players.push(this.createPlayer('Player 1', 'human'));

      // AI opponents
      const aiCount = settings.aiOpponentCount || 1;
      for (let i = 0; i < aiCount; i++) {
        players.push(
          this.createPlayer(`AI ${i + 1}`, 'ai', settings.aiDifficulty)
        );
      }
    } else if (settings.mode === 'local-multiplayer') {
      const count = Math.max(1, Math.min(4, settings.playerCount));
      for (let i = 0; i < count; i++) {
        const name = settings.playerNames?.[i] || `Player ${i + 1}`;
        players.push(this.createPlayer(name, 'human'));
      }
    } else {
      // multiplayer-realtime - start with one player, others join
      players.push(this.createPlayer('Player 1', 'human'));
    }

    this.state = {
      gameId: this.generateId(),
      players,
      currentPlayerIndex: 0,
      currentRound: 1,
      dice: this.createFreshDice(),
      rollsRemaining: MAX_ROLLS,
      gameStatus: { type: 'active' },
      startedAt: Date.now(),
      lastActionAt: Date.now(),
    };
  }

  private createPlayer(
    name: string,
    type: YahtzeePlayerType,
    difficulty?: YahtzeeDifficulty
  ): YahtzeePlayer {
    return {
      id: this.generateId(),
      name,
      type,
      difficulty,
      scorecard: { scores: {}, yahtzeeBonusCount: 0 },
    };
  }

  private createFreshDice(): Die[] {
    return Array.from({ length: NUM_DICE }, () => ({
      value: 1 as DieValue,
      held: false,
    }));
  }

  // =========================================================================
  // Dice Operations
  // =========================================================================

  rollDice(): Die[] {
    if (this.state.rollsRemaining <= 0) {
      return this.state.dice;
    }
    if (this.state.gameStatus.type !== 'active') {
      return this.state.dice;
    }

    this.state.dice = this.state.dice.map((die) => {
      if (die.held) return die;
      return {
        value: this.randomDieValue(),
        held: false,
      };
    });

    this.state.rollsRemaining--;
    this.state.lastActionAt = Date.now();
    return [...this.state.dice];
  }

  toggleHold(dieIndex: number): void {
    if (dieIndex < 0 || dieIndex >= NUM_DICE) return;
    if (this.state.rollsRemaining === MAX_ROLLS) return; // Must roll at least once
    if (this.state.rollsRemaining <= 0) return; // No more rolls left, no point in holding

    this.state.dice[dieIndex].held = !this.state.dice[dieIndex].held;
  }

  setHold(dieIndex: number, held: boolean): void {
    if (dieIndex < 0 || dieIndex >= NUM_DICE) return;
    if (this.state.rollsRemaining === MAX_ROLLS) return;

    this.state.dice[dieIndex].held = held;
  }

  private randomDieValue(): DieValue {
    return (Math.floor(Math.random() * 6) + 1) as DieValue;
  }

  // =========================================================================
  // Scoring
  // =========================================================================

  /**
   * Calculate the potential score for a category given current dice.
   * Does NOT consider Yahtzee bonus joker rules here - that is handled in scoreCategory.
   */
  calculateScore(category: ScoreCategory, dice: Die[]): number {
    const values = dice.map((d) => d.value);
    const counts = this.getDiceCounts(values);

    switch (category) {
      case 'ones': return this.sumOfValue(values, 1);
      case 'twos': return this.sumOfValue(values, 2);
      case 'threes': return this.sumOfValue(values, 3);
      case 'fours': return this.sumOfValue(values, 4);
      case 'fives': return this.sumOfValue(values, 5);
      case 'sixes': return this.sumOfValue(values, 6);
      case 'threeOfAKind': return this.hasNOfAKind(counts, 3) ? this.sumAll(values) : 0;
      case 'fourOfAKind': return this.hasNOfAKind(counts, 4) ? this.sumAll(values) : 0;
      case 'fullHouse': return this.isFullHouse(counts) ? FULL_HOUSE_SCORE : 0;
      case 'smallStraight': return this.isSmallStraight(values) ? SMALL_STRAIGHT_SCORE : 0;
      case 'largeStraight': return this.isLargeStraight(values) ? LARGE_STRAIGHT_SCORE : 0;
      case 'yahtzee': return this.isYahtzee(counts) ? YAHTZEE_SCORE : 0;
      case 'chance': return this.sumAll(values);
      default: return 0;
    }
  }

  /**
   * Score a category for the current player. Handles Yahtzee bonus/joker rules.
   * Returns the scoring result and advances the turn.
   */
  scoreCategory(category: ScoreCategory): ScoringResult {
    const player = this.getCurrentPlayer();
    if (!player) {
      return { category, score: 0, isValid: false };
    }

    // Check category not already scored
    if (player.scorecard.scores[category] !== undefined) {
      return { category, score: 0, isValid: false };
    }

    // Must have rolled at least once
    if (this.state.rollsRemaining === MAX_ROLLS) {
      return { category, score: 0, isValid: false };
    }

    const values = this.state.dice.map((d) => d.value);
    const counts = this.getDiceCounts(values);
    const isCurrentYahtzee = this.isYahtzee(counts);

    let score: number;

    // Yahtzee Bonus / Joker rules
    if (isCurrentYahtzee && player.scorecard.scores['yahtzee'] !== undefined) {
      if (player.scorecard.scores['yahtzee']! > 0) {
        // Already scored Yahtzee with 50 points - this is a bonus Yahtzee
        player.scorecard.yahtzeeBonusCount++;

        // Joker rules: must use corresponding upper section if available
        const yahtzeeValue = values[0];
        const upperCategory = this.getUpperCategoryForValue(yahtzeeValue);

        if (category === upperCategory || player.scorecard.scores[upperCategory] !== undefined) {
          // Score the chosen category with joker rules
          score = this.calculateJokerScore(category, values, counts);
        } else {
          // Must score the corresponding upper section first
          return { category, score: 0, isValid: false };
        }
      } else {
        // Yahtzee was scored as 0 - no bonus, but joker rules apply for lower section
        score = this.calculateJokerScore(category, values, counts);
      }
    } else {
      score = this.calculateScore(category, this.state.dice);
    }

    // Record the score
    player.scorecard.scores[category] = score;
    this.state.lastActionAt = Date.now();

    // Advance to next turn
    this.advanceTurn();

    return { category, score, isValid: true };
  }

  private calculateJokerScore(
    category: ScoreCategory,
    values: DieValue[],
    counts: number[]
  ): number {
    // When using joker rules (bonus Yahtzee), lower section categories
    // score their fixed values regardless of dice composition
    switch (category) {
      case 'ones': return this.sumOfValue(values, 1);
      case 'twos': return this.sumOfValue(values, 2);
      case 'threes': return this.sumOfValue(values, 3);
      case 'fours': return this.sumOfValue(values, 4);
      case 'fives': return this.sumOfValue(values, 5);
      case 'sixes': return this.sumOfValue(values, 6);
      case 'threeOfAKind': return this.sumAll(values);
      case 'fourOfAKind': return this.sumAll(values);
      case 'fullHouse': return FULL_HOUSE_SCORE;
      case 'smallStraight': return SMALL_STRAIGHT_SCORE;
      case 'largeStraight': return LARGE_STRAIGHT_SCORE;
      case 'yahtzee': return YAHTZEE_SCORE;
      case 'chance': return this.sumAll(values);
      default: return 0;
    }
  }

  private getUpperCategoryForValue(value: DieValue): ScoreCategory {
    const map: Record<DieValue, ScoreCategory> = {
      1: 'ones',
      2: 'twos',
      3: 'threes',
      4: 'fours',
      5: 'fives',
      6: 'sixes',
    };
    return map[value];
  }

  // =========================================================================
  // Score Helpers
  // =========================================================================

  private getDiceCounts(values: DieValue[]): number[] {
    const counts = [0, 0, 0, 0, 0, 0, 0]; // index 0 unused, 1-6 for die values
    for (const v of values) {
      counts[v]++;
    }
    return counts;
  }

  private sumOfValue(values: DieValue[], target: number): number {
    return values.filter((v) => v === target).reduce((sum, v) => sum + v, 0);
  }

  private sumAll(values: DieValue[]): number {
    return values.reduce((sum, v) => sum + v, 0);
  }

  private hasNOfAKind(counts: number[], n: number): boolean {
    return counts.some((c) => c >= n);
  }

  private isFullHouse(counts: number[]): boolean {
    const hasThree = counts.includes(3);
    const hasTwo = counts.includes(2);
    return hasThree && hasTwo;
  }

  private isSmallStraight(values: DieValue[]): boolean {
    const unique = [...new Set(values)].sort((a, b) => a - b);
    const straights = [[1, 2, 3, 4], [2, 3, 4, 5], [3, 4, 5, 6]];
    return straights.some((straight) =>
      straight.every((v) => unique.includes(v as DieValue))
    );
  }

  private isLargeStraight(values: DieValue[]): boolean {
    const unique = [...new Set(values)].sort((a, b) => a - b);
    if (unique.length !== 5) return false;
    const straights = [[1, 2, 3, 4, 5], [2, 3, 4, 5, 6]];
    return straights.some((straight) =>
      straight.every((v, i) => unique[i] === v)
    );
  }

  private isYahtzee(counts: number[]): boolean {
    return counts.includes(5);
  }

  // =========================================================================
  // Turn Management
  // =========================================================================

  private advanceTurn(): void {
    const nextPlayerIndex = (this.state.currentPlayerIndex + 1) % this.state.players.length;

    if (nextPlayerIndex === 0) {
      // All players have taken their turn in this round
      this.state.currentRound++;
    }

    this.state.currentPlayerIndex = nextPlayerIndex;

    // Check if game is over (all 13 rounds completed)
    if (this.state.currentRound > TOTAL_ROUNDS) {
      this.endGame();
    } else {
      // Reset dice for next turn
      this.state.dice = this.createFreshDice();
      this.state.rollsRemaining = MAX_ROLLS;
    }
  }

  private endGame(): void {
    // Determine winner
    let highScore = -1;
    let winnerId = '';

    for (const player of this.state.players) {
      const total = this.getTotalScore(player);
      if (total > highScore) {
        highScore = total;
        winnerId = player.id;
      }
    }

    this.state.gameStatus = { type: 'completed', winner: winnerId };
  }

  // =========================================================================
  // Score Totals
  // =========================================================================

  getUpperSectionTotal(player: YahtzeePlayer): number {
    let total = 0;
    for (const cat of UPPER_CATEGORIES) {
      total += player.scorecard.scores[cat] || 0;
    }
    return total;
  }

  getUpperBonus(player: YahtzeePlayer): number {
    return this.getUpperSectionTotal(player) >= UPPER_BONUS_THRESHOLD
      ? UPPER_BONUS_SCORE
      : 0;
  }

  getLowerSectionTotal(player: YahtzeePlayer): number {
    let total = 0;
    for (const cat of LOWER_CATEGORIES) {
      total += player.scorecard.scores[cat] || 0;
    }
    return total;
  }

  getYahtzeeBonusTotal(player: YahtzeePlayer): number {
    return player.scorecard.yahtzeeBonusCount * YAHTZEE_BONUS_SCORE;
  }

  getTotalScore(player: YahtzeePlayer): number {
    return (
      this.getUpperSectionTotal(player) +
      this.getUpperBonus(player) +
      this.getLowerSectionTotal(player) +
      this.getYahtzeeBonusTotal(player)
    );
  }

  // =========================================================================
  // Query Methods
  // =========================================================================

  getState(): YahtzeeGameState {
    return { ...this.state };
  }

  getCurrentPlayer(): YahtzeePlayer | null {
    if (this.state.players.length === 0) return null;
    return this.state.players[this.state.currentPlayerIndex];
  }

  getAvailableCategories(player?: YahtzeePlayer): ScoreCategory[] {
    const p = player || this.getCurrentPlayer();
    if (!p) return [];
    return ALL_CATEGORIES.filter((cat) => p.scorecard.scores[cat] === undefined);
  }

  getDice(): Die[] {
    return [...this.state.dice];
  }

  getRollsRemaining(): number {
    return this.state.rollsRemaining;
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type === 'completed';
  }

  getWinner(): YahtzeePlayer | null {
    if (this.state.gameStatus.type !== 'completed') return null;
    const winnerId = this.state.gameStatus.winner;
    return this.state.players.find((p) => p.id === winnerId) || null;
  }

  getWinnerId(): string | null {
    if (this.state.gameStatus.type === 'completed') {
      return this.state.gameStatus.winner;
    }
    return null;
  }

  /**
   * Get potential scores for all available categories given current dice.
   */
  getPotentialScores(): Record<ScoreCategory, number> {
    const result = {} as Record<ScoreCategory, number>;
    for (const cat of ALL_CATEGORIES) {
      result[cat] = this.calculateScore(cat, this.state.dice);
    }
    return result;
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  setState(state: YahtzeeGameState): void {
    this.state = state;
  }

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(data: string): void {
    try {
      this.state = JSON.parse(data);
    } catch (e) {
      console.error('Failed to deserialize Yahtzee state:', e);
    }
  }

  // =========================================================================
  // Utilities
  // =========================================================================

  private generateId(): string {
    return `yahtzee_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
}
